import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type DashboardLayout = 'sidebar' | 'header';

interface PreferencesState {
  // ── Sidebar ──
  sidebarExpanded: boolean;    // pinned state, driven by the sidebar chevron
  sidebarOpenOnHover: boolean; // expand the collapsed sidebar while hovered
  // ── Mobile navigation (ephemeral — never persisted) ──
  mobileNavOpen: boolean;      // slide-in drawer open state on < lg viewports
  // ── Navigation ──
  defaultLandingPage: string;
  // ── Layout (desktop only; mobile always uses the drawer + bottom nav) ──
  dashboardLayout: DashboardLayout;
  // ── One-time "you can leave this page" tip on async tool runs ──
  seenAsyncRunTip: { detector: boolean; journalFinder: boolean };

  setSidebarExpanded: (v: boolean) => void;
  toggleSidebarExpanded: () => void;
  setSidebarOpenOnHover: (v: boolean) => void;
  openMobileNav: () => void;
  closeMobileNav: () => void;
  toggleMobileNav: () => void;
  setDefaultLandingPage: (v: string) => void;
  setDashboardLayout: (v: DashboardLayout) => void;
  markAsyncRunTipSeen: (tool: 'detector' | 'journalFinder') => void;
}

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set) => ({
      sidebarExpanded: false,
      sidebarOpenOnHover: true,
      mobileNavOpen: false,
      defaultLandingPage: '/dashboard',
      dashboardLayout: 'sidebar',
      seenAsyncRunTip: { detector: false, journalFinder: false },

      setSidebarExpanded: (v) => set({ sidebarExpanded: v }),
      toggleSidebarExpanded: () => set((s) => ({ sidebarExpanded: !s.sidebarExpanded })),
      setSidebarOpenOnHover: (v) => set({ sidebarOpenOnHover: v }),
      openMobileNav: () => set({ mobileNavOpen: true }),
      closeMobileNav: () => set({ mobileNavOpen: false }),
      toggleMobileNav: () => set((s) => ({ mobileNavOpen: !s.mobileNavOpen })),
      setDefaultLandingPage: (v) => set({ defaultLandingPage: v }),
      setDashboardLayout: (v) => set({ dashboardLayout: v }),
      markAsyncRunTipSeen: (tool) =>
        set((s) => ({ seenAsyncRunTip: { ...s.seenAsyncRunTip, [tool]: true } })),
    }),
    {
      name: 'preferences-storage',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      // v1: hover-open became the platform default. Flip it on once for existing
      // users who persisted the old `false`. They can re-disable it in Settings.
      migrate: (persistedState, version) => {
        if (version < 1) {
          return { ...(persistedState as object), sidebarOpenOnHover: true } as PreferencesState;
        }
        return persistedState as PreferencesState;
      },
      partialize: (state) => ({
        sidebarExpanded: state.sidebarExpanded,
        sidebarOpenOnHover: state.sidebarOpenOnHover,
        defaultLandingPage: state.defaultLandingPage,
        dashboardLayout: state.dashboardLayout,
        seenAsyncRunTip: state.seenAsyncRunTip,
      }),
    }
  )
);

/** Landing-page options shared by the Settings select. */
export const LANDING_PAGE_OPTIONS: { value: string; label: string }[] = [
  { value: '/dashboard', label: 'Dashboard' },
  { value: '/ai-tools/detector', label: 'AI Detector' },
  { value: '/ai-tools/humanizer', label: 'Humanizer' },
  { value: '/ai-tools/journal-finder', label: 'Journal Finder' },
  { value: '/files', label: 'Files' },
  { value: '/notifications', label: 'Notifications' },
];
